import React, { Suspense, useState, useEffect, useRef, useMemo } from 'react';
import { Canvas, useThree, useLoader, useFrame, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { useGLTF, Loader, shaderMaterial } from '@react-three/drei';
import { TextureLoader } from 'three';
import bgModel from '../3d/backgroundSite5.glb';
import spaceBG from '../3d/spaceBg.jpg';
import { throttle } from 'lodash';
import '../styles/background.css';

//universal change cursor style function
function useCursorStyle(cursorStyle) {
  useEffect(() => {
    //when the component mounts or cursorStyle changes, update the document's cursor style
    document.body.style.cursor = cursorStyle;

    //reset cursor
    return () => {
      document.body.style.cursor = '';
    };
  }, [cursorStyle]);
}

function Model( { pointerPosition } ) {
  const model = useRef();
  const groupRef = useRef();
  const { camera, mouse } = useThree();
  const [topIsHovered, setTopIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [secondOrbIsHovered, setSecondOrbIsHovered] = useState(false);
  const [cubeContainerHovered, setCubeContainerHovered] = useState(false);
  const [isRotating, setIsRotating] = useState(false); 
  const lastRotationZRef = useRef(0);
  const [crystalHover, setCrystalHover] = useState(false); 
  const [crystalRotating, setIsRotatingCrystal] = useState(false); 
  const { scene } = useGLTF(bgModel);
  const orbOriginalPosition = useRef(new THREE.Vector3());
  const dragOffset = useRef(new THREE.Vector2());
  const dragStartPosition = useRef(new THREE.Vector2(0, 0));
  const rotatingStartXRef = useRef(0); 
  const initialRotationYRef = useRef(0);

  useCursorStyle(topIsHovered ? 'grab' : '');
  useCursorStyle(secondOrbIsHovered ? 'pointer' : '');
  useCursorStyle(cubeContainerHovered ? 'grab' : '');
  useCursorStyle(crystalHover ? 'grab' : '');


  //coordinate position references
  const originalY1Ref = useRef(0);
  const originalY2Ref = useRef(0);
  const originalCrystalRef = useRef(0);

  //animated object variables
  const rotatingGroup1 = scene.getObjectByName('rotating-2');
  const rotatingGroup2 = scene.getObjectByName('center-light-structure');
  const rotatingGroup3 = scene.getObjectByName('rotating-1');
  const rotatingGroup4 = scene.getObjectByName('rotating-3');
  const crystal = scene.getObjectByName('crystal');
  const powerCube = scene.getObjectByName('powerCube');
  const topEnergyOrb = scene.getObjectByName('top-energy');
  const floatingContainer1 = scene.getObjectByName('floating-container1');
  const floatingContainer2 = scene.getObjectByName('floating-container2');
  const energyOrb = scene.getObjectByName('energy-orb');

  //top energy ref for hover/click effects
  const topEnergyOrbRef = useRef();
  if (topEnergyOrb) {
    topEnergyOrbRef.current = topEnergyOrb; 
  } else {
    console.error('Top Energy Orb not found in the scene');
  }

  //orb energy ref for hover effects
  const energyOrbRef = useRef();
  if (energyOrbRef) {
    energyOrbRef.current = energyOrb; 
  } else {
    console.error('Energy Orb not found in the scene');
  }

  //cube rotating container ref for effects
  const rotatingGroup2Ref = useRef();
  if (rotatingGroup2Ref) {
    rotatingGroup2Ref.current = rotatingGroup2; 
  } else {
    console.error('Cube container not found in the scene');
  }

  //cube rotating container ref for effects
  const crystalRef = useRef();
  if (crystalRef) {
    crystalRef.current = crystal; 
  } else {
    console.error('Crystal not found in the scene');
  }


  //initialize Raycaster to use for mouse intersects
  useFrame(() => {
    const raycaster = new THREE.Raycaster();
    if (pointerPosition && topEnergyOrbRef.current && energyOrbRef.current) {
      raycaster.setFromCamera(pointerPosition, camera);
      const intersects = raycaster.intersectObject(topEnergyOrbRef.current, true);
      const intersects2 = raycaster.intersectObject(energyOrbRef.current, true);
      const intersects3 = raycaster.intersectObject(rotatingGroup2Ref.current, true);
      const intersects4 = raycaster.intersectObject(crystalRef.current, true);

      setTopIsHovered(intersects.length > 0);
      setSecondOrbIsHovered(intersects2.length > 0);
      setCubeContainerHovered(intersects3.length > 0);
      setCrystalHover(intersects4.length > 0);
    }
  });

  //save original coordinates of topEnergyOrb, ensures reference to starting position before drag operations
  useEffect(() => {
    if (topEnergyOrb) {
      orbOriginalPosition.current.copy(topEnergyOrb.position);
    }
  }, [topEnergyOrb]);

  //creates listener for mouse interaction with topEnergyOrb to enable dragging functionality, adjusts cursor style while dragging
  //the mouse move listener adjusts the orb's position based on the drag, applying constraints to the movement range
  //releasing the mouse button stops the dragging process and resets the cursor style
  useEffect(() => {
    const handleMouseDown = (event) => {
        if (topIsHovered) {
          setIsDragging(true);
          document.body.style.cursor = 'grabbing'; 
          dragStartPosition.current.set(event.clientX, event.clientY);
          event.preventDefault();
        }
        if (cubeContainerHovered && rotatingGroup2Ref.current) {
          setIsRotating(true);
          rotatingStartXRef.current = event.clientX; //save the starting mouse X position
          initialRotationYRef.current = rotatingGroup2Ref.current.rotation.y; //save the starting rotation Y
          document.body.style.cursor = 'grabbing'; 
          event.preventDefault();
        }
        if (crystalHover && crystalRef.current) {
          setIsRotatingCrystal(true);
          rotatingStartXRef.current = event.clientX; //save the starting mouse X position
          initialRotationYRef.current = crystalRef.current.rotation.y; //save the starting rotation Y
          document.body.style.cursor = 'grabbing'; 
          event.preventDefault();
        }
    };

    //hook for mouse being pressed down
    const handleMouseMove = (event) => {
        if (isDragging || !topEnergyOrbRef.current){
          //calculate delta based on current mouse position and start position.
          const dx = (event.clientX - dragStartPosition.current.x) / window.innerWidth * 6; 
          const dy = -(event.clientY - dragStartPosition.current.y) / window.innerHeight * 6;
          
          //update the orb's position, clamping to ensure it doesn't move too far.
          dragOffset.current.set(THREE.MathUtils.clamp(dx, -3, 3), THREE.MathUtils.clamp(dy, -3, 3));
          topEnergyOrbRef.current.position.x = orbOriginalPosition.current.x + dragOffset.current.x;
          topEnergyOrbRef.current.position.y = orbOriginalPosition.current.y + dragOffset.current.y;
        }

        if (isRotating && rotatingGroup2Ref.current) {
          const deltaX = event.clientX - rotatingStartXRef.current;
          const rotationDelta = deltaX * 0.01; // Adjust this value as needed
          rotatingGroup2Ref.current.rotation.z = initialRotationYRef.current + rotationDelta;
          //lastRotationZRef.current = rotatingGroup2Ref.current.rotation.z;
      }
      
        if (crystalRotating && crystalRef.current) {
          const deltaX = event.clientX - rotatingStartXRef.current;
          const rotationDelta = deltaX * 0.01; 
          crystalRef.current.rotation.z = initialRotationYRef.current + rotationDelta;
        }
    };

    const handleMouseUp = () => {
      if (isRotating && rotatingGroup2Ref.current) {
        // Ensure the last rotation is captured when the user stops interacting
        lastRotationZRef.current = rotatingGroup2Ref.current.rotation.z;
      }

      setIsDragging(false);
      setIsRotating(false); 
      setIsRotatingCrystal(false); 
      document.body.style.cursor = ''; //reset cursor to default
    };

    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [topIsHovered, isDragging, cubeContainerHovered, isRotating, crystalHover, crystalRotating]); 

  //move orb back to original position after dragging in a smooth manner
  useFrame(() => {
      if (!isDragging && topEnergyOrbRef.current) {
          //use linear interpolation (lerp) for smooth transition of positions over time
          topEnergyOrbRef.current.position.lerp(orbOriginalPosition.current, 0.1);
      }
  });

  useEffect(() => {
    if (topEnergyOrb && crystal) {
      //crystal transparency
      crystal.material.transparent = false; 
      crystal.material.opacity = 0.9;

      //energy orb transparency
      energyOrb.material.transparent = true; 
      energyOrb.material.opacity = 0.75; 

      //alpha blending
      //crystal.material.blending = THREE.AdditiveBlending;
      topEnergyOrb.material.blending = THREE.AdditiveBlending;
      
      //grab positions of floating objects
      originalY2Ref.current = floatingContainer2.position.y;
      originalY1Ref.current = floatingContainer1.position.y;
      originalCrystalRef.current = crystal.position.y;
    }
  }, [scene, setTopIsHovered]);

  //set positions of floating objects by default
  floatingContainer1.position.y = 70;
  floatingContainer2.position.y = 52;
  crystal.position.y = 60;

  //defines lightweight custom hook to change emissive property over time for energyOrb
  const useEmissiveTransition = (isHovered) => {
    const [emissiveIntensity, setEmissiveIntensity] = useState(isHovered ? 10 : 0.8);
    useEffect(() => {
      //intensities
      const targetIntensity = isHovered ? 10 : 0.8;
      //define duration
      const transitionDuration = 1375; //1.375 seconds
      const startTime = Date.now();

      const updateIntensity = () => {
        const elapsedTime = Date.now() - startTime;
        const progress = Math.min(elapsedTime / transitionDuration, 1);
        //use THREE.MathUtils.lerp to calculate the current intensity
        const currentIntensity = THREE.MathUtils.lerp(emissiveIntensity, targetIntensity, progress);
        setEmissiveIntensity(currentIntensity);

        if (progress < 1) {
          requestAnimationFrame(updateIntensity);
        }
      };
      updateIntensity();
    }, [isHovered]);

    return emissiveIntensity;
  };

  //save intensity hook as const
  const emissiveIntensity = useEmissiveTransition(secondOrbIsHovered);


  const interactionStartTimeRef = useRef(0);
  const totalInteractionTimeRef = useRef(0);
  const lastInteractionEndTimeRef = useRef(0);
  //use the useFrame hook from react-three-fiber to rotate the group on every frame
  useFrame(({ clock, delta }) => {
      const elapsedTime = clock.getElapsedTime();
      //const baseRotationSpeed = 0.25; // Base speed when not scrolling
      //const scrollingRotationSpeed = 1; // Increased speed when scrolling
      //const rotationSpeed = isScrolling ? scrollingRotationSpeed : baseRotationSpeed;
      const minScale = 5.5;
      const maxScale = 7.5;
      const scaleRange = (maxScale - minScale) / 1.5;

      if (topEnergyOrbRef.current) {
        if (isDragging) {
          //apply drag offset to orb's position
          topEnergyOrbRef.current.position.x = orbOriginalPosition.current.x + dragOffset.current.x;
          topEnergyOrbRef.current.position.y = orbOriginalPosition.current.y + dragOffset.current.y;
        } else {
          //interpolate back to original position
          topEnergyOrbRef.current.position.lerp(orbOriginalPosition.current, 0.1); //interpolation seed
        }
      }


      if (energyOrb && topEnergyOrb) {
          //first orbiting structure
          //rotatingGroup1.rotation.y = elapsedTime * 0.3;
          

        //second cube encircling orbiting structure
        if (isRotating && interactionStartTimeRef.current === 0) {
          interactionStartTimeRef.current = elapsedTime;
        }
        //when the user stops interacting, calculate and accumulate total interaction time
        if (!isRotating && interactionStartTimeRef.current !== 0) {
          rotatingGroup2Ref.current.rotation.z = lastRotationZRef.current;
          const interactionEndTime = elapsedTime;
          totalInteractionTimeRef.current += interactionEndTime - interactionStartTimeRef.current;
          interactionStartTimeRef.current = 0; 
          lastInteractionEndTimeRef.current = elapsedTime; //keep track of when the interaction ended
        }
        const effectiveElapsedTime = elapsedTime - totalInteractionTimeRef.current;
        //use `effectiveElapsedTime` for rotation calculations outside user interaction
        if (!isRotating) {
          rotatingGroup2.rotation.z = (effectiveElapsedTime * 1);
        } 


        //rotating cube
        powerCube.rotation.y = elapsedTime * 4;
        powerCube.rotation.z = elapsedTime * 4;
        if (isRotating){
          powerCube.rotation.y = elapsedTime * 8;
          powerCube.rotation.z = elapsedTime * 8;
        }
        //orbiting structure around cube
        rotatingGroup3.rotation.y = -(elapsedTime * 0.5);
        
        //crystal rotating
        crystal.rotation.y = elapsedTime * 1;

        //crystal.rotation.x = elapsedTime * 1;

        //third orbiting structure
        //rotatingGroup4.rotation.y = elapsedTime * 0.25;

        //mid energy orb
        if (secondOrbIsHovered) {        
          const minScaleOrb = 2.2;
          const maxScaleOrb = 3.2;
          const scaleAmplitudeOrb = (maxScaleOrb - minScaleOrb) / 2;
          const scaleOrb = minScaleOrb + scaleAmplitudeOrb + Math.sin(elapsedTime) * scaleAmplitudeOrb;
          energyOrb.scale.set(scaleOrb, scaleOrb, scaleOrb);
          energyOrb.rotation.y = elapsedTime * 10;
          energyOrb.material.transparent = true; 
          energyOrb.material.opacity = 0.8; 
          energyOrb.material.emissive = new THREE.Color(0x5e97f2); 
          energyOrb.material.emissiveIntensity = emissiveIntensity;
        }
        else{
          const minScaleOrb = 2.2;
          const maxScaleOrb = 3.2;
          const scaleAmplitudeOrb = (maxScaleOrb - minScaleOrb) / 2;
          const scaleOrb = minScaleOrb + scaleAmplitudeOrb + Math.sin(elapsedTime) * scaleAmplitudeOrb;
          energyOrb.scale.set(scaleOrb, scaleOrb, scaleOrb);
          energyOrb.rotation.y = elapsedTime * 4;
          energyOrb.material.transparent = true; 
          energyOrb.material.opacity = 0.5; 
          energyOrb.material.emissive = new THREE.Color(0x5e97f2); 
          energyOrb.material.emissiveIntensity = emissiveIntensity;
        }

        //top energy orb 
        //testing interactability
        if (topIsHovered || isDragging) {
          const scale = minScale + scaleRange + Math.sin(elapsedTime) * scaleRange;
          topEnergyOrb.scale.set(scale, scale, scale);
          topEnergyOrb.rotation.y = (elapsedTime * 6); 
          //topEnergyOrb.MeshStandardMaterial.emissive = 'blue';
          topEnergyOrb.material.transparent = true; 
          topEnergyOrb.material.opacity = 0.9; 
        } else {
          const scale = minScale + scaleRange + Math.sin(elapsedTime) * scaleRange;
          topEnergyOrb.scale.set(scale, scale, scale);
          topEnergyOrb.rotation.y = elapsedTime * 2.5; 
          topEnergyOrb.material.opacity = 1;
          topEnergyOrb.material.transparent = false;
        }

        //floating anims
        const floatingHeight = 0.8; 
        //use wave functions to simulate floating
        if (floatingContainer1) {
          floatingContainer1.position.y = originalY1Ref.current + Math.sin(elapsedTime) * floatingHeight;
        }
        if (floatingContainer2) {
          floatingContainer2.position.y = originalY2Ref.current + Math.cos(elapsedTime) * floatingHeight;
        }
        if (crystal) {
          crystal.position.y = originalCrystalRef.current + Math.sin(elapsedTime/1.5) * 1;
        }
    }
  });

  //console.log("Full model loaded", scene);
  return <primitive object={scene} />;
}
 
//camera movement control function
function CameraController() {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(-18, 138, 25);
    camera.lookAt(-18, 138, 25);
    camera.updateProjectionMatrix();
  }, []);

  const desktopCameraPositions = [
    { x: -18, y: 140, z: 30 },
    { x: -20, y: 120, z: 30 },
    { x: -20, y: 116, z: 30 },
    { x: -20, y: 101, z: 30 },
    { x: -20, y: 98, z: 30 },
    { x: -15, y: 85, z: 30 },
    { x: -10, y: 28, z: 42 },
  ];

  //define a new set of camera positions for mobile
  const mobileCameraPositions = [
    { x: 0, y: 140, z: 35 },
    { x: 0, y: 115, z: 38 },
    { x: 0, y: 100, z: 35 },
    { x: 0, y: 75, z: 35 },
    { x: 0, y: 50, z: 40 },
    { x: 0, y: 28, z: 52 },
    { x: 0, y: 28, z: 52 }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const totalScrollHeight = document.body.scrollHeight - window.innerHeight;
      const scrollProgress = window.scrollY / totalScrollHeight;

      //different camera position for mobile users
      const isMobile = window.innerWidth < 800;
      const cameraPositions = isMobile ? mobileCameraPositions : desktopCameraPositions;

      //determine current segment and progress within that segment
      const segmentLength = 1 / (cameraPositions.length - 1);
      const currentSegmentIndex = Math.min(
        Math.floor(scrollProgress / segmentLength),
        cameraPositions.length - 2
      );
      const segmentProgress = (scrollProgress % segmentLength) / segmentLength;

      //interpolate position between current and next segment positions
      const startPosition = cameraPositions[currentSegmentIndex];
      const endPosition = cameraPositions[currentSegmentIndex + 1];
      const interpolatedPosition = {
        x: startPosition.x + (endPosition.x - startPosition.x) * segmentProgress,
        y: startPosition.y + (endPosition.y - startPosition.y) * segmentProgress,
        z: startPosition.z + (endPosition.z - startPosition.z) * segmentProgress,
      };

      //camera interpolation
      camera.position.set(interpolatedPosition.x, interpolatedPosition.y, interpolatedPosition.z);
      camera.lookAt(interpolatedPosition.x, interpolatedPosition.y, interpolatedPosition.z);
      camera.updateProjectionMatrix();
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [camera]); //add dependencies as needed

  return null;
}


function EnvironmentBackground() {
  const { scene } = useThree();
  const texture = useLoader(TextureLoader, spaceBG);
  scene.background = texture; 
  
  return null;
}

function BluePointLight({ position, color, intensity, distance, decay}) {
  return <pointLight position={position} color={color} intensity={intensity} distance={distance} decay={decay} />;
}

function Background() {
  const [pointerPosition, setPointerPosition] = useState({ x: 0, y: 0 });

  //handle pointer movement and update pointerPosition state
  const handlePointerMove = (event) => {
    const x = (event.clientX / window.innerWidth) * 2 - 1;
    const y = -(event.clientY / window.innerHeight) * 2 + 1;
    setPointerPosition({ x, y });
  };

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, []);
  
  return (
    <Suspense fallback={<Loader />}>
      <div className="canvas-container"> {/* Apply the CSS class here */}
      <Canvas>
          <EnvironmentBackground />
          <ambientLight intensity={0.5} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
          <pointLight position={[-10, -10, -10]} />
          <Model pointerPosition={pointerPosition} />
          <CameraController />
          <BluePointLight position = {[-3, 150, 5]} color="#5e97f2" intensity={1000} distance={200} decay={1.5}/>
          <BluePointLight position = {[0, 113, -2]} color="#5e97f2" intensity={1000} distance={500} decay={2}/>
          <BluePointLight position = {[0, 94, 0]} color="#5e97f2" intensity={300} distance={100} decay={3}/>
          <BluePointLight position = {[0, 60, 0]} color="#5e97f2" intensity={1500} distance={500} decay={2}/>
        </Canvas>
      </div>
      <div className="static-overlay"></div>
      <div className="black-overlay"></div>
    </Suspense>
  );
}

export default Background;
